import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { INJURY_CATEGORIES, ILLNESSES } from '../athlete/HealthFlagModal';

/**
 * Coach-side health-flag editor — handles both edit and create.
 *
 * Two modes, picked by whether `flag` is provided:
 *
 *   Edit mode  (flag is an existing health_flags row):
 *     - Type is locked (injury vs illness can't change here —
 *       cleaner as a delete + recreate).
 *     - All fields prefilled from the row.
 *     - Footer shows Delete (window.confirm gated).
 *
 *   Create mode (flag is null, athleteId required):
 *     - Top of modal shows an Injury / Illness type picker.
 *     - flag_date defaults to `defaultDate` (the day modal context)
 *       or today.
 *     - No Delete button.
 *
 * Both modes share the same field layout below the type row.
 *
 * Surfaced from AthleteProfile's day-log modal:
 *   - Edit row → opens with `flag={...}`.
 *   - "+ Log injury or illness" button → opens with `flag={null}`,
 *     `athleteId={...}`, `defaultDate={...}`.
 */
export function CoachHealthFlagModal({
  flag,
  athleteId,
  defaultDate,
  onClose,
  onSaved,
  onDeleted,
}) {
  const isEdit = !!flag;
  const todayStr = new Date().toISOString().slice(0, 10);

  const [flagType, setFlagType] = useState(flag?.type ?? 'injury');
  const [flagDate, setFlagDate] = useState(flag?.flag_date ?? defaultDate ?? todayStr);
  const [bodyPart, setBodyPart] = useState(flag?.body_part ?? '');
  const [painScale, setPainScale] = useState(flag?.pain_scale ?? '');
  const [injuryCategory, setInjuryCategory] = useState(flag?.injury_category ?? '');
  const [illnessType, setIllnessType] = useState(flag?.illness_type ?? '');
  const [notes, setNotes] = useState(flag?.notes ?? '');
  const [resolved, setResolved] = useState(!!flag?.resolved);
  const [resolvedAt, setResolvedAt] = useState(
    flag?.resolved_at ? flag.resolved_at.slice(0, 10) : todayStr
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Reset every state slot when the surfaced flag changes — covers
  // the coach hopping straight from one row's Edit to another's,
  // and switching between edit and create modes.
  useEffect(() => {
    setFlagType(flag?.type ?? 'injury');
    setFlagDate(flag?.flag_date ?? defaultDate ?? todayStr);
    setBodyPart(flag?.body_part ?? '');
    setPainScale(flag?.pain_scale ?? '');
    setInjuryCategory(flag?.injury_category ?? '');
    setIllnessType(flag?.illness_type ?? '');
    setNotes(flag?.notes ?? '');
    setResolved(!!flag?.resolved);
    setResolvedAt(flag?.resolved_at ? flag.resolved_at.slice(0, 10) : todayStr);
    setErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flag?.id, athleteId, defaultDate]);

  const isInjury = flagType === 'injury';

  async function save() {
    setBusy(true);
    setErr('');

    const fields = {
      flag_date: flagDate || todayStr,
      notes: notes.trim() || null,
      resolved,
      resolved_at: resolved
        ? new Date(resolvedAt + 'T12:00:00').toISOString()
        : null,
    };
    if (isInjury) {
      fields.body_part = bodyPart.trim() || null;
      fields.pain_scale = painScale === '' ? null : Number(painScale);
      fields.injury_category = injuryCategory || null;
      fields.illness_type = null;
    } else {
      fields.illness_type = illnessType || null;
      fields.body_part = null;
      fields.body_part_id = null;
      fields.body_sub_part = null;
      fields.pain_scale = null;
      fields.injury_category = null;
    }

    let error;
    if (isEdit) {
      ({ error } = await supabase
        .from('health_flags')
        .update(fields)
        .eq('id', flag.id));
    } else {
      ({ error } = await supabase
        .from('health_flags')
        .insert({
          ...fields,
          athlete_id: athleteId,
          type: flagType,
          // Coach-created entries don't go through the body diagram
          // so we leave the spatial coordinates null.
          body_part_id: null,
          body_sub_part: null,
        }));
    }

    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onSaved?.();
    onClose?.();
  }

  async function handleDelete() {
    if (!isEdit) return;
    if (!window.confirm('Delete this flag? This can\'t be undone.')) return;
    setBusy(true);
    // .select() after delete returns the actually-deleted rows
    // so we can detect an RLS-silent no-op and surface it
    // instead of letting the modal close on a phantom success.
    const { data, error } = await supabase
      .from('health_flags')
      .delete()
      .eq('id', flag.id)
      .select();
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    if (!data || data.length === 0) {
      setErr(
        "Couldn't delete this flag — your account may not have permission. " +
        "Check the coach delete policy on health_flags."
      );
      return;
    }
    onDeleted?.();
    onClose?.();
  }

  const headlineEmoji = isInjury ? '🩹' : '🤒';
  const headlineLabel = isEdit
    ? `Edit ${isInjury ? 'injury' : 'illness'}`
    : `Log ${isInjury ? 'injury' : 'illness'}`;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,24,44,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 10, padding: 20,
          width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>
            {headlineEmoji} {headlineLabel}
          </h2>
          <button
            onClick={onClose}
            disabled={busy}
            style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--gray-400)', cursor: 'pointer', padding: 0 }}
            aria-label="Close"
          >×</button>
        </div>

        {!isEdit && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 6 }}>
              Type
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {[
                { id: 'injury',  label: '🩹 Injury' },
                { id: 'illness', label: '🤒 Illness' },
              ].map(t => {
                const active = flagType === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFlagType(t.id)}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1.5px solid',
                      borderColor: active ? 'var(--navy)' : 'var(--gray-200)',
                      background: active ? 'var(--navy)' : '#fff',
                      color: active ? '#fff' : 'var(--gray-700)',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="form-group" style={{ marginBottom: 12 }}>
          <label className="form-label">Started</label>
          <input
            type="date"
            className="form-input"
            value={flagDate}
            onChange={e => setFlagDate(e.target.value)}
          />
        </div>

        {isInjury ? (
          <>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Body part</label>
              <input
                type="text"
                className="form-input"
                value={bodyPart}
                onChange={e => setBodyPart(e.target.value)}
                placeholder="e.g. Left calf"
              />
            </div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Category</label>
                <select
                  className="form-input form-select"
                  value={injuryCategory ?? ''}
                  onChange={e => setInjuryCategory(e.target.value)}
                >
                  <option value="">—</option>
                  {INJURY_CATEGORIES.map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ width: 120, marginBottom: 0 }}>
                <label className="form-label">Pain (0–10)</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  className="form-input"
                  value={painScale}
                  onChange={e => setPainScale(e.target.value)}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Illness</label>
            <select
              className="form-input form-select"
              value={illnessType ?? ''}
              onChange={e => setIllnessType(e.target.value)}
            >
              <option value="">—</option>
              {ILLNESSES.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group" style={{ marginBottom: 12 }}>
          <label className="form-label">Notes</label>
          <textarea
            className="form-input"
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Anything else worth recording?"
          />
        </div>

        <div
          style={{
            background: 'var(--gray-50)',
            border: '1px solid var(--gray-200)',
            borderRadius: 8,
            padding: 12,
            marginBottom: 14,
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--gray-700)', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={resolved}
              onChange={e => setResolved(e.target.checked)}
            />
            {isEdit ? 'Mark as resolved' : 'Set an end date'}
          </label>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4, marginLeft: 26 }}>
            {isEdit
              ? 'Closes the flag and stamps when the athlete recovered.'
              : 'Leave unchecked if it\'s ongoing. Check it to record a finished span (e.g. backdating an old injury or illness).'}
          </div>
          {resolved && (
            <div className="form-group" style={{ marginTop: 10, marginBottom: 0 }}>
              <label className="form-label">Ended on</label>
              <input
                type="date"
                className="form-input"
                value={resolvedAt}
                onChange={e => setResolvedAt(e.target.value)}
                min={flagDate || undefined}
              />
            </div>
          )}
        </div>

        {err ? (
          <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{err}</div>
        ) : null}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {isEdit ? (
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              style={{
                background: 'none',
                border: 'none',
                color: '#dc2626',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                padding: '8px 0',
              }}
            >
              Delete flag
            </button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-xs btn-xs-ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={save}
              disabled={busy || (!isEdit && !athleteId)}
              style={{ width: 'auto', padding: '8px 16px' }}
            >
              {busy ? 'Saving…' : isEdit ? 'Save' : 'Log it'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
